<template>
  <div class="charts-row">
    <!-- Traffic Trend Chart -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-box">
          <i class="fas fa-chart-line header-icon icon-orange"></i>
          <div>
            <h3 class="chart-title">Lượt truy cập & Người dùng</h3>
            <p class="chart-subtitle">
              Số lượt sự kiện (visits) và người dùng hoạt động (users) trong {{ rangeLabel }}
            </p>
          </div>
        </div>
      </div>
      <div class="chart-body">
        <apexchart
          v-if="hasTrafficData"
          type="area"
          height="280"
          :options="trafficChartOptions"
          :series="trafficChartSeries"
        ></apexchart>
        <el-empty v-else description="Không có dữ liệu lượt truy cập"></el-empty>
      </div>
    </div>

    <!-- User Growth Chart -->
    <div class="chart-card">
      <div class="card-header">
        <div class="title-box">
          <i class="fas fa-chart-line header-icon icon-green"></i>
          <div>
            <h3 class="chart-title">Tăng trưởng người dùng</h3>
            <p class="chart-subtitle">
              Tổng số lượng người dùng tích lũy trong {{ rangeLabel }}
            </p>
          </div>
        </div>
      </div>
      <div class="chart-body">
        <apexchart
          v-if="hasUserGrowthData"
          type="area"
          height="280"
          :options="userGrowthChartOptions"
          :series="userGrowthChartSeries"
        ></apexchart>
        <el-empty v-else description="Không có dữ liệu tăng trưởng người dùng"></el-empty>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'TrafficUserCharts',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    trafficTrend: {
      type: Array,
      default: () => []
    },
    userGrowth: {
      type: Array,
      default: () => []
    },
    rangeLabel: {
      type: String,
      default: '30 phút qua'
    }
  },
  computed: {
    hasTrafficData() {
      return this.trafficTrend && this.trafficTrend.length > 0
    },
    hasUserGrowthData() {
      return this.userGrowth && this.userGrowth.length > 0
    },

    // 1. Traffic Trend Options & Series
    trafficChartSeries() {
      return [
        {
          name: 'Lượt truy cập',
          data: this.trafficTrend.map((t) => t.visits || 0)
        },
        {
          name: 'Người dùng',
          data: this.trafficTrend.map((t) => t.users || 0)
        }
      ]
    },
    trafficChartOptions() {
      const categories = this.trafficTrend.map((t) => t.date || '')
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'inherit',
          sparkline: { enabled: false }
        },
        colors: ['#f59e0b', '#3b82f6'],
        stroke: {
          curve: 'smooth',
          width: 2.5
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px', fontWeight: 500 }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px' }
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 4
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '13px',
          fontWeight: 600,
          markers: { radius: 12 }
        },
        tooltip: {
          theme: 'light'
        }
      }
    },

    // 2. User Growth Options & Series
    userGrowthChartSeries() {
      return [
        {
          name: 'Người dùng tích lũy',
          data: this.userGrowth.map((u) => u.totalUsers || 0)
        }
      ]
    },
    userGrowthChartOptions() {
      const categories = this.userGrowth.map((u) => u.date || '')
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        colors: ['#10b981'],
        stroke: {
          curve: 'smooth',
          width: 2.5
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px', fontWeight: 500 }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: { colors: '#9ca3af', fontSize: '11px' }
          }
        },
        grid: {
          borderColor: '#f3f4f6',
          strokeDashArray: 4
        },
        legend: { show: false },
        tooltip: {
          theme: 'light'
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.charts-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

.chart-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid #f1f3f5;

  .card-header {
    margin-bottom: 20px;

    .title-box {
      display: flex;
      align-items: flex-start;
      gap: 10px;

      .header-icon {
        font-size: 16px;
        margin-top: 3px;
        &.icon-orange { color: #f59e0b; }
        &.icon-green { color: #10b981; }
      }

      .chart-title {
        font-size: 16px;
        font-weight: 700;
        color: #1f2937;
        margin: 0;
      }

      .chart-subtitle {
        font-size: 13px;
        color: #6b7280;
        margin: 4px 0 0 0;
      }
    }
  }

  .chart-body {
    min-height: 280px;
  }
}
</style>
